import { createSignal, onMount, Show, useContext, For, Accessor } from 'solid-js';
import { Button } from '../../ui-components/button/button';
import { Text } from '../../ui-components/text/text';
import { FormProps, FieldType, FormValue, DropdownOption } from './forms.types';
import { GraphQLClient, gql } from 'graphql-request';
import { countries } from '../../tools/countries-data';
import { AppContext } from '../../app-context-provider/app-context-provider';
import { ErrorCatcher } from '../../tools/error-catcher';
import { validate } from './validation';
import { StyledLoadingSpinner } from '../loading-spinner/loading-spinner.styles';
import { useLocation, useSearchParams } from '@solidjs/router';
import { Checkbox } from '../../ui-components/inputs/checkbox/checkbox';
import { Radio } from '../../ui-components/inputs/radio/radio';
import { Link } from '../../ui-components/link/link';
import { StyledHorizontalSpace, StyledVerticalSpace } from '../../ui-components/utility-style-components/spacing';
import { Heading } from '../../ui-components/heading/heading';
import { validateCaptcha } from '../../tools/validate-captcha';
import { StyledFlexColumn } from '../../ui-components/utility-style-components/flex';
import { AngleDownIcon } from '../icons-library/angle-down';
import { CheckCircleIcon } from '../icons-library/check-circle';
import { EventContext } from '../event/event-context-provider';
import { atosGlobalCountrySlugToCountryCode } from '../../tools/countries-data';
import { getPlaceholder } from '../registration-form/helpers';
import theme from '../../style/theme';
import { FieldErrorMessage, FormInput, FormSelect, FormSelectLabel, FormSelectWrapper, IconWrapper } from './helpers';

const getUniqueSlug = (field: FieldType) => {
    if (field.extraSettings?.name?.value) {
        return field.extraSettings.name.value;
    }

    return field.slug;
    // We had this previously, but changing to use the name given in Gutenberg so we can pass the data to salesforce
    // with the correct value, matching what salesforce expects.
    // Not sure if this can cause any issues, so i'm keeping the below as a reference.
    // return `${field.slug}-${field.instance}`;
};

const client = new GraphQLClient('/api');

const POST_FORM_QUERY = gql`
    mutation PostForm (
        $type: String,
        $siteCountry: String,
        $input: salesforce_PostFormInput,
        $referer: String!
        $captchaToken: String!
        $gotoEventId: String
        $campaignId: String
    ) {
        postForm(
            type: $type,
            siteCountry: $siteCountry,
            input: $input,
            referer: $referer,
            captchaToken: $captchaToken,
            gotoEventId: $gotoEventId,
            campaignId: $campaignId,
        ) {
            success
        }
    }
`;


type Errors = Record<string, string>;



export const FormsTw = (props: FormProps) => {
    const AppState = useContext(AppContext);
    const { gotoEventId, isDisabledEvent } = useContext(EventContext);
    const { localize, privacyPolicyPage, recaptchaSiteKey, siteInfo, siteDomains, productContactFormUrl } = AppState;
    const location = useLocation();
    const [searchParams] = useSearchParams();

    const [loading, setLoading] = createSignal(false);
    const [error, setError] = createSignal('');
    const [errors, setErrors] = createSignal<Errors>({});
    const [success, setSuccess] = createSignal('');
    const [currentStepIndex, setCurrentStepIndex] = createSignal<number>(0);
    let formRef: HTMLDivElement | undefined;

    onMount(() => {
        if (!document.querySelector('#grecaptcha')) {
            const scriptTag = document.createElement('script');
            scriptTag.type = 'text/javascript';
            scriptTag.id = 'grecaptcha';
            scriptTag.src = `https://www.google.com/recaptcha/enterprise.js?render=${recaptchaSiteKey}`;
            document.head.appendChild(scriptTag);
        }
    });

    const nextStepClick = () => {
        const valid = validateFields(props.steps[ currentStepIndex() ].fields);

        if (valid) {
            setCurrentStepIndex(currentStepIndex() + 1);
            formRef?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
    };

    const previousStepClick = () => {
        // We don't need to validate if the user is going back a step.
        setCurrentStepIndex(currentStepIndex() - 1);
    };

    const isFinalStep = () => currentStepIndex() === props.steps.length - 1;

    const formHasMultipleSteps = () => props.steps.length > 1;


    const USER_TYPES = [
        // Keeping the values here exactly as Salesforce expects them, hence the uppercase first Letter.
        {
            value: 'HCP',
            text: localize('i-am-a-healthcare-professional', 'I am a healthcare professional'),
        },
        {
            value: 'Patient',
            text: localize('i-am-a-patient', 'I am a patient'),
        },
        {
            value: 'Relative',
            text: localize('i-am-a-relative', 'I am a relative'),
        },
        {
            value: 'Other',
            text: localize('other', 'Other'),
        },
    ];

    const isBusinessLead = () => formValues().userType === 'HCP' || formValues().userType === 'Other';


    const setInitialValues = () => {
        const initialObj = props.steps[currentStepIndex()].fields?.reduce((obj: FormValue, field) => {
            const { slug } = field;
            if (slug === 'customDropdown' || slug === 'textArea' || slug === 'textField') {
                const uniqueSlug = getUniqueSlug(field);
                obj[uniqueSlug] = '';
            } else if (field.slug === 'hiddenField') {
                const uniqueSlug = getUniqueSlug(field);
                obj[uniqueSlug] = field.extraSettings?.fieldValue?.value;
            } else {
                obj[field.slug] = '';
            }
            return obj;
        }, {});
        initialObj.acceptPolicy = false;

        if (siteInfo.siteSlug) {
            // Prepopulate the country field with the value that the site they're visiting has. No matter which step it is in.
            const currentSelectCountryCode = atosGlobalCountrySlugToCountryCode[siteInfo.siteSlug as keyof typeof atosGlobalCountrySlugToCountryCode];
            const currentSelectCountry = countries.find(country => country.code === currentSelectCountryCode);
            if (currentSelectCountry) {
                initialObj.country = currentSelectCountry;
            }
        }
        return initialObj;
    };

    const [formValues, setFormValues] = createSignal<FormValue>(setInitialValues());

    const handleInputChange = (fieldSlug: string, fieldValue: any, customInputSlug?: string) => {
        const newFormValues = { ...formValues() };
        if (fieldSlug === 'country') {
            // We want the whole country object here, not only the code.
            const country = countries.find(country => country.code === fieldValue);
            newFormValues[fieldSlug] = country;
        } else {
            newFormValues[fieldSlug] = fieldValue;
        }

        // If the form has errors, we want to remove it if it's fine now.
        const hasErrors = errors()[fieldSlug];

        setFormValues(newFormValues);

        if (hasErrors) {
            const field = !customInputSlug ? props.steps[currentStepIndex()].fields.find((field) => field.slug === fieldSlug) : props.steps[currentStepIndex()].fields.find((field) => field.slug === customInputSlug);
            if (field) {
                // Returns nothing, just sets signals so we're up to date.
                validateFields([field]);
            }
        }
    };

    // Eloqua expects these values to look like: {'checkboxGroupName': 'value1; value2; value3'}
    const handleCheckboxChange = (checkboxGroupName: string, checkboxValue: any, checked: boolean) => {
        const newFormValues = { ...formValues() };

        const checkboxValues = newFormValues[checkboxGroupName] ? newFormValues[checkboxGroupName].split(';') : [];
        if (checked) {
            checkboxValues.push(checkboxValue);
        } else {
            const index = checkboxValues.indexOf(checkboxValue);
            checkboxValues.splice(index, 1);
        }
        const checkboxValueString = checkboxValues.join(';');
        newFormValues[checkboxGroupName] = checkboxValueString;

        setFormValues(newFormValues);

        // If the checkbox has errors, we want to remove it if it's fine now.
        const hasErrors = errors()[checkboxGroupName];

        if (hasErrors) {
            const field = props.steps[currentStepIndex()].fields.find((field) => field.extraSettings?.name?.value === checkboxGroupName);
            if (field) {
                // Returns nothing, just sets signals so we're up to date.
                validateFields([field]);
            }
        }
    };

    const handleRadioButtonChange = (radioButtonGroupName: string, value: string) => {
        setFormValues({ ...formValues(), [radioButtonGroupName]: value });

        // If the radiobutton had errors, we want to remove it if it's fine now.
        const hasErrors = errors()[radioButtonGroupName];

        if (hasErrors) {
            const field = props.steps[currentStepIndex()].fields.find((field) => field.extraSettings?.name?.value === radioButtonGroupName);
            if (field) {
                // Returns nothing, just sets signals so we're up to date.
                validateFields([field]);
            }
        }
    };

    const handleDropdownChange = (dropdownGroupName: string, value: string) => {
        setFormValues({ ...formValues(), [dropdownGroupName]: value });

        // If the dropdown had errors, we want to remove it if it's fine now.
        const hasErrors = errors()[dropdownGroupName];

        if (hasErrors) {
            const field = props.steps[currentStepIndex()].fields.find((field) => field.extraSettings?.name?.value === dropdownGroupName);
            if (field) {
                // Returns nothing, just sets signals so we're up to date.
                validateFields([field]);
            }
        }
    };


    const handleCheck = (fieldSlug: string) => {
        const newFormValues = { ...formValues() };
        newFormValues[fieldSlug] = !newFormValues[fieldSlug];
        setFormValues(newFormValues);
        const hasErrors = errors()[fieldSlug];
        if (hasErrors) {
            const field = props.steps[currentStepIndex()].fields.find((field) => field.slug === fieldSlug);
            if (field) {
                // Returns nothing, just sets signals so we're up to date.
                validateFields([field]);
            }
        }
    };

    const isPolicyAccepted = () => {
        const checked = formValues()[ 'acceptPolicy' ];

        if (!checked) {
            const msg = `${localize('please-agree-to-terms', 'Please agree to the terms and conditions before submitting')}`;
            setErrors({ ...errors(), [ 'acceptPolicy' ]: msg });
            return false;
        } else {
            const currentErrors = { ...errors() };
            delete currentErrors[ 'acceptPolicy' ];
            setErrors(currentErrors);
            return true;
        }
    };

    const handleSend = async (e: any) => {
        e.preventDefault();

        setLoading(true);
        setErrors({});
        setError('');

        const extraFields = [];
        if (isBusinessLead()) {
            extraFields.push({
                name: 'Company',
                slug: 'company',
                required: true,
            });
        }

        // Check if the final step's fields are valid
        const validFields = validateFields([...props.steps[currentStepIndex()].fields, ...extraFields]);
        if (!isPolicyAccepted() || !validFields) {
            setLoading(false);
            return;
        }

        try {
            const captchaToken = await validateCaptcha('submit_form', recaptchaSiteKey);            

            if (!captchaToken) {
                throw new Error('Captcha validation failed - probably a bot.');
            }

            const currentSite = siteDomains.find((domain) => domain.siteId === siteInfo.siteId);
            const productForm = productContactFormUrl?.includes(location.pathname);

            let formType;
            if (siteInfo.siteType === 'atos-care') {
                formType = 'contactAtosCare';

                if (gotoEventId) {
                    formType = 'goto-event';
                }
            } else {
                formType = 'contact';

                if (productForm) {
                    formType = 'product';
                }
                if (gotoEventId) {
                    formType = 'goto-event';
                }
            }

            const formAnswers = { ...formValues() };
            delete formAnswers.acceptPolicy;

            const variables = {
                type: formType,
                referer: window.location.href,
                siteCountry: currentSite?.country || '',
                captchaToken,
                gotoEventId,
                input: {
                    formData: {
                        ...formAnswers,
                        ...searchParams?.product && { leadHook: searchParams?.product }, // If we have a product in the url, we add it to the form data as a lead hook.
                    },
                },
                campaignId : props.formCampaignId,
            };

            const res: any = await client.request(POST_FORM_QUERY, variables);

            if (res.postForm.success) {
                formRef?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                setSuccess(localize('form-submit-success', 'Form submitted'));
                setFormValues(setInitialValues());
            } else {
                throw new Error();
            }
        } catch (e) {
            console.log('Error submitting form: ', e);
            formRef?.scrollIntoView({ behavior: 'smooth', block: 'start' });
            setError(localize('form-submit-error', 'Error submitting the form'));
        }

        setLoading(false);
    };


    const validateFields = (fields: FieldType[]) => {
        setErrors({}); // Reset errors before validation

        fields.forEach((field) => {
            const { slug } = field;
            if (slug === 'customCheckbox' || slug === 'customDropdown' || slug === 'customRadioButtons' || slug === 'customTextField' || slug === 'textArea') {
                const { extraSettings, required } = field;
                if (!extraSettings) return;
                const { name } = extraSettings;
                const value = formValues()[name.value];
                if (required && (!value || value.length === 0)) {
                    let fieldName = '';
                    
                    if (field.slug === 'customCheckbox') {
                        fieldName = field.extraSettings?.placeholder.value || '';
                    } else if (field.slug === 'customDropdown') {
                        fieldName = field.extraSettings?.placeholder.value || '';
                    } else if (field.slug === 'customRadioButtons') {
                        fieldName = field.extraSettings?.placeholder.value || '';
                    } else if (field.slug === 'customTextField') {
                        fieldName = field.extraSettings?.placeholder.value || '';
                    } else if (field.slug === 'textArea') {
                        fieldName = field.extraSettings?.placeholder.value || '';
                    }
                    
                    const translation = getPlaceholder({...field, name: fieldName, required: false}, localize);

                    const msg = `${translation} ${localize('is-required', 'is required')}`;
                    setErrors({ ...errors(), [name.value]: msg });
                } else {
                    const currentErrors = { ...errors() };
                    delete currentErrors[name.value];
                    setErrors(currentErrors);
                }
            } else {
                const key = field.instance ? `${field.slug}-${field.instance}` : field.slug;
                const value = formValues()[key];

                if (field.required && (!value || (typeof value === 'string' && !value.trim()))) {
                    const translation = getPlaceholder({...field, required: false}, localize);
                    const msg = `${translation} ${localize('is-required', 'is required')}`;
                    setErrors({ ...errors(), [field.slug]: msg });
                    return false;
                }

                // Not required but no value is fine.
                if (!value) {
                    return;
                }

                // If we have a value, we validate it.
                const { valid, errorMessage } = validate(key, value, localize);
                if (errorMessage) {
                    setErrors({ ...errors(), [field.slug]: errorMessage });
                } else if (valid) {
                    const currentErrors = { ...errors() };
                    delete currentErrors[field.slug];
                    setErrors(currentErrors);
                }
            }
        });

        // Special case: Company field injected programmatically
        if (isBusinessLead()) {
            const stepIncludesUserType = props.steps[currentStepIndex()].fields.some(
                (field: FieldType) => field.slug === 'userType'
            );
            // a double check to make sure the current dom actually has a company input
            const companyInput = document.querySelector<HTMLInputElement>('[name="company"]');
            if (stepIncludesUserType && companyInput) {
                const value = formValues()['company'];
                if (!value || !value.trim()) {
                    setErrors({
                        ...errors(),
                        company: `${localize('company', 'Company')} ${localize('is-required', 'is required')}`,
                    });
                    return false;
                }
            }
        }

        if (Object.keys(errors()).length > 0) {
            return false;
        }

        return true;
    };

    const renderInput = (args: { field: FieldType; forceRequire?: Accessor<boolean>; }) => {
        const { field, forceRequire } = args;

        let translationSlug = field.slug;

        switch (field.slug) {
            case 'firstname':
                translationSlug = 'first-name';
                break;
            case 'lastname':
                translationSlug = 'last-name';
                break;
            case 'zip':
                translationSlug = 'postal-code';
                break;
            default:
                break;
        }

        let autocomplete;
        let type;
        if (field.slug === 'email') {
            autocomplete = 'email';
            type = 'email';
        } else if (field.slug === 'phone') {
            autocomplete = 'tel';
            type = 'tel';
        } else if (field.slug === 'company') {
            autocomplete = 'organization';
        }

        const placeholder = `${localize(translationSlug, field.name)}${field.required || (forceRequire && forceRequire()) ? '*' : ''}`;

        return (
            <div>
                <FormInput
                    required={field.required || (forceRequire && forceRequire())}
                    value={formValues()[field.slug]}
                    onChange={(e) => handleInputChange(field.slug, e.currentTarget.value)}
                    placeholder={placeholder}
                    hasErrors={errors()[field.slug] ? true : false}
                    name={field.slug}
                    aria-label={localize(translationSlug, field.name)}
                    aria-describedby={`desc-${field.slug}`}
                    title={placeholder}
                    {...autocomplete
                        ? { autocomplete }
                        : {}
                    }
                    type={type}
                />

                <span id={`desc-${field.slug}`} style={{display: 'none'}}>
                    {placeholder}
                </span>

                <Show when={errors()[field.slug]}>
                    <FieldErrorMessage errors={errors} fieldKey={field.slug} />
                </Show>
            </div>
        );
    };

    // Very similar to renderInput, but this comes from a custom text field in WP, and the slug/name/eloqua-value 
    // should not be the slug of the field, but instead a value coming from WP, written by the editors.
    const renderTextField = (field: FieldType) => {
        const { extraSettings } = field;
        if (!extraSettings) return;

        const placeholder = `${field.extraSettings?.placeholder?.value || localize('text-field', 'Text field')}` + (field.required ? '*' : '');

        const { name } = extraSettings;

        const uniqueSlug = getUniqueSlug(field);

        return (
            <div>
                <FormInput
                    name={'custom-text-field-' + name.value}
                    required={field.required}
                    value={formValues()[uniqueSlug]}
                    onChange={((e) => handleInputChange(name.value, e.currentTarget.value, field.slug))}
                    placeholder={placeholder}
                    hasErrors={errors()[uniqueSlug] ? true : false}
                    aria-label={placeholder}
                    aria-describedby={`desc-${uniqueSlug}`}
                    title={placeholder}
                />

                <span id={`desc-${uniqueSlug}`} style={{display: 'none'}}>
                    {placeholder}
                </span>

                <Show when={(errors()[uniqueSlug])}>
                    <FieldErrorMessage errors={errors} fieldKey={uniqueSlug} />
                </Show>
            </div>
        );

    };        

    const renderDropdown = (field: FieldType) => {
        const { extraSettings, required } = field;

        if (!extraSettings) return;
        const { info, placeholder, name, options } = extraSettings;

        // Backwards compability for when the dropdown options were coming from info.value (different WP setup).
        const optionsArray = options?.value ? options.value.split('\n') : info.value.split(',');
        const formatedOptions: DropdownOption[] = optionsArray.map((v: string) => {
            const label = v.charAt(0).toUpperCase() + v.slice(1);
            return {
                value: v,
                text: label,
            };
        }).filter((option: DropdownOption) => option.value);

        const placeholderText = `${placeholder.value}${required ? '*' : ''}`;

        return (
            <div>
                <FormSelectWrapper>    
                    <FormSelectLabel for={`custom-dropdown-select-${name.value}`} id={`custom-dropdown-select-label-${name.value}`}>
                        {placeholderText}
                    </FormSelectLabel>
                    <FormSelect
                        id={`custom-dropdown-select-${name.value}`}
                        required={required}
                        value={formValues()[name.value]}
                        hasErrors={errors()[name.value] ? true : false}
                        onChange={((e) => handleDropdownChange(name.value, e.currentTarget.value))}
                        name={'custom-dropdown-' + name.value}
                        aria-labelledby={`custom-dropdown-select-label-${name.value}`}
                        aria-describedby={`desc-${name.value}`}
                        title={placeholderText}
                    >
                        <option value="">{placeholderText.replace('*', '')}</option>
                        {
                            formatedOptions.map((option) => (
                                <option value={option.value}>{option.text}</option>
                            ))
                        }
                    </FormSelect>

                    <IconWrapper>
                        <AngleDownIcon height={4.8} width={1.8} min-width="1.2" min-height="1.2" fill="#707070" ariaHidden={true}/>
                    </IconWrapper>
                </FormSelectWrapper>

                <span id={`desc-${name.value}`} style={{display: 'none'}}>
                    {placeholder.value}
                </span>

                <Show when={(errors()[name.value])}>
                    <FieldErrorMessage errors={errors} fieldKey={name.value} />
                </Show>
            </div>
        );
    };


    const renderCheckbox = (field: FieldType) => {
        const { required, extraSettings } = field;

        if (!extraSettings) return;

        const { options: checkboxOptions, name } = extraSettings;
        const placeholder = required ? extraSettings.placeholder.value + '*' : extraSettings.placeholder.value;
        const options: DropdownOption[] = checkboxOptions.value
            .split('\n')
            .map((v: string) => v.trim())
            .map((v: string) => {
                const label = v.charAt(0).toUpperCase() + v.slice(1);
                return {
                    value: v,
                    text: label,
                };
            })
            .filter((option: DropdownOption) => option.value);

        return (
            <div>
                <StyledVerticalSpace size={1} />
                <Heading tag="h4" variant="temporarySmall">{placeholder}</Heading>
                <ul>
                    <For each={options}>
                        {(option, i) => {
                            return (
                                <li>
                                    <Checkbox
                                        isChecked={false}
                                        whenClicked={(e: any) => handleCheckboxChange(name.value, option.value, e.target.checked)}
                                        value={option.value}
                                        name={option.text}
                                        inputName={'custom-checkbox-' + name.value}
                                        id={`checkbox-${option.value}-${i()}`}
                                        aria-label={option.text}
                                        aria-describedby={`desc-${name.value}`}
                                        title={placeholder}
                                    />
                                </li>
                            );
                        }}
                    </For>
                </ul>

                <span id={`desc-${name.value}`} style={{display: 'none'}}>
                    {placeholder}
                </span>

                <Show when={(errors()[name.value])}> 
                    <FieldErrorMessage errors={errors} fieldKey={name.value} />
                </Show>
            </div>
        );
    };


    const renderTextArea = (field: FieldType) => {
        const placeholder = `${field.extraSettings?.placeholder?.value || localize('text-area', 'Text area')}` + (field.required ? '*' : '');

        const uniqueSlug = getUniqueSlug(field);

        return (
            <div>
                <textarea
                    class="w-full text-[1.6rem] font-atos-regular rounded-[5px] p-[1rem] resize-none border"
                    classList={{
                        'border-pink': !!errors()[uniqueSlug],
                        'border-[#707070]': !errors()[uniqueSlug]
                    }}
                    required={field.required}
                    value={formValues()[uniqueSlug]}
                    onChange={(e) =>
                        handleInputChange(uniqueSlug, e.currentTarget.value, field.slug)
                    }
                    placeholder={placeholder}
                    name={'custom-text-area-' + field.slug}
                    rows={6}
                    aria-label={placeholder}
                    aria-describedby={`desc-${uniqueSlug}`}
                    title={placeholder}
                />



                <span id={`desc-${uniqueSlug}`} style={{display: 'none'}}>
                    {placeholder}
                </span>

                <Show when={(errors()[uniqueSlug])}>
                    <FieldErrorMessage errors={errors} fieldKey={uniqueSlug} />
                </Show>
            </div>
        );
    };

    const renderComment = (field: FieldType) => {
        const uniqueSlug = getUniqueSlug(field);

        return (
            <div>
                <textarea
                    class="w-full text-[1.6rem] font-atos-regular rounded-[5px] p-[1rem] resize-none border"
                    classList={{
                        'border-pink': !!errors()[uniqueSlug],
                        'border-[#707070]': !errors()[uniqueSlug]
                    }}
                    required={field.required}
                    value={formValues()[uniqueSlug]}
                    onChange={((e) => handleInputChange(uniqueSlug, e.currentTarget.value))}
                    placeholder={`${localize('comments', 'Comments')}${field.required ? '*' : ''}`}
                    name={uniqueSlug}
                    rows={1}
                    aria-label={localize('comments', 'Comments')}
                    aria-describedby={`desc-${uniqueSlug}`}
                    title={localize('comments', 'Comments')}
                />

                <span id={`desc-${uniqueSlug}`} style={{display: 'none'}}>
                    {localize('comments', 'Comments')}
                </span>

                <Show when={(errors()[uniqueSlug])}>
                    <FieldErrorMessage errors={errors} fieldKey={uniqueSlug} />
                </Show>
            </div>
        );
    };

    const renderHiddenField = () => {
        const hiddenFields = props.steps[currentStepIndex()].fields.filter(f => f.slug === 'hiddenField');
        if (hiddenFields && hiddenFields.length) {
            return hiddenFields.map((fieldData) => {
                const uniqueSlug = getUniqueSlug(fieldData);
                return (
                    <input
                        name={uniqueSlug}
                        hidden={true}
                    />
                );
            });
        }
    };

    const renderRadioButtons = (field: FieldType) => {
        const { extraSettings, required } = field;

        if (!extraSettings) return;

        const { options: radioButtonOptions, name } = extraSettings;
        const placeholder = required ? extraSettings.placeholder.value + '*' : extraSettings.placeholder.value;
        const options: DropdownOption[] = radioButtonOptions.value
            .split('\n')
            .map((v: string) => v.trim())
            .map((v: string) => {
                const label = v.charAt(0).toUpperCase() + v.slice(1);
                return {
                    value: v,
                    text: label,
                };
            })
            .filter((option: DropdownOption) => option.value);
        return (
            <div>
                <StyledVerticalSpace size={1} />
                <Heading tag="h4" variant="temporarySmall">{placeholder}</Heading>
                <ul>
                    <For each={options}>
                        {(option: { value: string; text: string }, i) => (
                            <li>
                                <Radio
                                    whenClicked={(() => handleRadioButtonChange(name.value, option.value))}
                                    value={option.value}
                                    readableName={option.text}
                                    isChecked={false}
                                    name={'custom-radio-' + name.value}
                                    id={`radio-${option.value}-${i()}`}
                                    aria-label={option.text}
                                    aria-describedby={`desc-${name.value}`}
                                    title={placeholder}
                                />
                            </li>
                        )}
                    </For>
                </ul>
                <span id={`desc-${name.value}`} style={{display: 'none'}}>{placeholder}</span>
                <Show when={(errors()[name.value])}> 
                    <FieldErrorMessage errors={errors} fieldKey={name.value} />
                </Show>
            </div>
        );
    };

    const renderCountry = (field: FieldType) => {
        const title = `${localize('country', 'Country')}${field.required ? '*' : ''}`;

        const options: DropdownOption[] = countries.map((country) => {
            return {
                value: country.code,
                text: country.name,
            };
        });

        const currentSelectCountryCode = atosGlobalCountrySlugToCountryCode[siteInfo.siteSlug as keyof typeof atosGlobalCountrySlugToCountryCode];

        return (
            <div>
                <FormSelectLabel for="country-select" id="country-select-label">
                    {title}
                </FormSelectLabel>

                <FormSelectWrapper>
                    <FormSelect
                        id="country-select"
                        name={field.slug}
                        required={field.required}
                        value={formValues().country?.code}
                        hasErrors={errors()[field.slug] ? true : false}
                        onChange={(e) => handleInputChange(field.slug, e.currentTarget.value)}
                        aria-labelledby="country-select-label"
                        aria-describedby={`desc-${field.slug}`}
                        title={title}
                        autocomplete='country'
                    >
                        <option value="">{title.replace('*', '')}</option>
                        {options.map((option) => {
                            return <option selected={option.value === currentSelectCountryCode} value={option.value}>{option.text}</option>;
                        })}

                    </FormSelect>

                    <IconWrapper>
                        <AngleDownIcon height={1.8} width={1.8} min-width="1.2" min-height="1.2" fill="#707070" ariaHidden={true} />
                    </IconWrapper>
                </FormSelectWrapper>

                <span id={`desc-${field.slug}`} style={{ display: 'none' }}>
                    {title}
                </span>

                <Show when={errors()[field.slug]}>
                    <FieldErrorMessage errors={errors} fieldKey={field.slug} />
                </Show>
            </div>
        );
    };


    const renderUserType = (field: FieldType) => {
        const options: DropdownOption[] = USER_TYPES;
        const placeholder = `${localize('user-type', 'User type')}${field.required ? '*' : ''}`;

        return (
            <div>
                <FormSelectLabel for="user-type-select" id="user-type-select-label">
                    {placeholder}
                </FormSelectLabel>

                <FormSelectWrapper>
                    <FormSelect
                        id="user-type-select"
                        name={field.slug}
                        required={field.required}
                        value={formValues()[field.slug]}
                        hasErrors={errors()[field.slug] ? true : false}
                        onChange={((e) => handleInputChange(field.slug, e.currentTarget.value))}
                        aria-labelledby="user-type-select-label"
                        aria-describedby={`desc-${field.slug}`}
                        title={placeholder}
                    >
                        <option value="">{placeholder.replace('*', '')}</option>
                        {
                            options.map((option) => {
                                return (
                                    <option value={option.value}>{option.text}</option>
                                );
                            })
                        }
                    </FormSelect>

                    <IconWrapper>
                        <AngleDownIcon height={1.8} width={1.8} min-width="1.2" min-height="1.2" fill="#707070" ariaHidden={true} />
                    </IconWrapper>
                </FormSelectWrapper>

                <span id={`desc-${field.slug}`} style={{display: 'none'}}>
                    {placeholder}
                </span>

                <Show when={(errors()[field.slug])}>
                    <FieldErrorMessage errors={errors} fieldKey={field.slug} />
                </Show>
            </div>
        );
    };


    const renderCommunicationPreference = (field: FieldType) => {
        const placeholder = `${localize('communication-preference', 'Communication preference')}${field.required ? '*' : ''}`;

        return (
            <div>
                <FormSelectLabel for="communication-preference-select" id="communication-preference-select-label">
                    {placeholder}
                </FormSelectLabel>

                <FormSelectWrapper>
                    <FormSelect
                        id="communication-preference-select"
                        name={field.slug}
                        required={field.required}
                        value={formValues()[field.slug]}
                        hasErrors={errors()[field.slug] ? true : false}
                        onChange={((e) => handleInputChange(field.slug, e.currentTarget.value))}
                        aria-labelledby="communication-preference-select-label"
                        aria-describedby={`desc-${field.slug}`}
                        title={placeholder}
                    >
                        <option value="">{placeholder.replace('*', '')}</option>
                        <option value={'Phone'}>{localize('phone', 'Phone')}</option>
                        <option value={'Email'}>{localize('email', 'E-mail')}</option>
                    </FormSelect>

                    <IconWrapper>
                        <AngleDownIcon height={1.8} width={1.8} min-width="1.2" min-height="1.2" fill="#707070" ariaHidden={true} />
                    </IconWrapper>
                </FormSelectWrapper>

                <span id={`desc-${field.slug}`} style={{ display: 'none' }}>
                    {placeholder}
                </span>

                <Show when={(errors()[field.slug])}> 
                    <FieldErrorMessage errors={errors} fieldKey={field.slug} />
                </Show>
            </div>
        );
    };


    // This should always be at the bottom, for now at least.
    const renderPolicy = () => {
        return (
            <>
                <div class="flex flex-wrap flex-row items-center gap-[1rem]">
                    <Checkbox
                        isChecked={formValues()?.acceptPolicy}
                        whenClicked={() => {
                            handleCheck('acceptPolicy');
                        }}
                        value="privacy-policy-consent"
                        name={localize('agree-to-terms', 'I agree to the terms and conditions')}
                        aria-label={localize('agree-to-terms', 'I agree to the terms and conditions')}
                        aria-describedby="desc-acceptPolicy"
                    />

                    <span id="desc-acceptPolicy" style={{ display: 'none' }}>
                        {localize('agree-to-terms', 'I agree to the terms and conditions')}
                    </span>

                    <Link noBlockSpacing={true} label={localize('read-more-here', 'Read more here')} url={privacyPolicyPage || '/'} />
                </div>

                <Show when={errors()['acceptPolicy']}>
                    <FieldErrorMessage errors={errors} fieldKey={'acceptPolicy'} />
                </Show>
            </>
        );
    };

    const renderCompanyField = () => {
        // If user selects HCP or Other (i.e. a "business lead"), the company field should be required/mandatory.
        // However, if it's not even added in the WP form we need to manually add it down here.
        if (!isBusinessLead()) return null;

        // Check all steps for the existance of the company field
        const formHasCompanyField = props.steps.some(step =>
            step.fields.some((field: FieldType) => field.slug === 'company')
        );

        if (formHasCompanyField) {
            // If the company field is already present, we don't need to render it again.
            return null;
        }

        const stepIncludesUserType = props.steps[currentStepIndex()].fields.some((field: FieldType) => {
            return field.slug === 'userType';
        });

        if (!stepIncludesUserType) {
            // If the userType field is not in the current step, we don't need to render the company field. This avoids rendering the company field on every step.
            return null;
        }

        const field = {
            slug: 'company',
            name: 'Company',
            required: true,
            extraSettings: {
                translation: { value: '' },
                info: { value: '' },
                link: { value: '' },
                linkText: { value: '' },
                placeholder: { value: 'Company' },
                name: { value: 'company' },
                options: { value: '', text: '' },
                fieldValue: { value: '' },
            },
        };
        return renderInput({ field, forceRequire: () => true });
    };

    const productContactTitle = () => {
        // This ensures that the pre- and post-text are only added if they are not empty, taking into account required spaces.
        const preTextTranslationExists = localize('product-contact-form-pre-text', '') !== '';
        const preText = preTextTranslationExists ? `${localize('product-contact-form-pre-text', '')} ` : '';
        const postText = localize('product-contact-form-post-text', '');

        return `${preText + searchParams?.product} ${postText}`;
    };

    const StepFields = () => (
        <>
            <For each={props.steps[ currentStepIndex() ].fields}>{(field: FieldType) => {
                const { slug } = field;

                // We don't want to render the policy field here, as it's rendered separately.
                if (slug === 'acceptPolicy') {
                    return null;
                }

                // Handle the special cases first.
                if (slug === 'company') {
                    return renderInput({ field, forceRequire: isBusinessLead });
                }

                const slugFunctionMap = {
                    'country': renderCountry,
                    'hiddenField': renderHiddenField,
                    'userType': renderUserType,
                    'comments': renderComment,
                    'communicationPreference': renderCommunicationPreference,
                    'customDropdown': renderDropdown,
                    'customTextField': renderTextField,
                    'customRadioButtons': renderRadioButtons,
                    'customCheckbox': renderCheckbox,
                    'textArea': renderTextArea,
                };

                if (Object.keys(slugFunctionMap).includes(slug)) {
                    return slugFunctionMap[ slug as keyof typeof slugFunctionMap ](field);
                }

                // Default to text input field.
                return renderInput({ field });
            }}</For>

            {renderCompanyField()}
        </>
    );

    const NavigationButtons = () => (
        <div class="flex justify-between items-center mt-[1rem]">
            <Show when={currentStepIndex() !== 0} fallback={<div />}>
                <Button
                    label={localize('previous', 'Previous')}
                    variant='tertiary'
                    onClick={previousStepClick}
                />
            </Show>

            <Show
                when={!isFinalStep()}
                fallback={
                    <Button
                        label={localize('form-submit', 'Submit')}
                        variant='primary'
                        onClick={handleSend}
                        disabled={loading()}
                    />
                }
            >
                <Button
                    label={localize('next', 'Next')}
                    variant='primary'
                    onClick={nextStepClick}
                />
            </Show>
        </div>
    );

    const FinalStepFixedElements = () => (
        <StyledFlexColumn gap='0.5rem'>
            {renderPolicy()}

            <Show when={!success()}>
                <StyledVerticalSpace size={1} />
                <StyledFlexColumn>
                    <Text fontSize='small' fontStyle='italic' color='darkGray' noBlockSpacing={true}>
                        {localize('recaptcha-policy-and-terms-disclaimer', 'This site is protected by reCAPTCHA and the Google Privacy Policy and Terms of Service apply.')}
                    </Text>
                    <StyledHorizontalSpace size={1} />
                    <Text fontSize='small' fontStyle='italic' color='darkGray'>
                        {`<a href="https://policies.google.com/privacy">${localize('recaptcha-privacy-policy', 'Privacy Policy')}</a> | <a href="https://policies.google.com/terms">${localize('recaptcha-terms-of-service', 'Terms of Service')}</a>`}
                    </Text>
                </StyledFlexColumn>
            </Show>

            <StyledHorizontalSpace size={2} />

            <Show when={error()} fallback={<div />}>
                <div class="flex items-center h-[4rem] mx-auto text-primary text-[16px] font-atos-family">{error()}</div>
            </Show>

            <Show when={loading()} fallback={<div/>}>
                <div class="flex items-center h-[4rem] mx-auto">
                    <StyledLoadingSpinner />
                </div>
            </Show>
        </StyledFlexColumn>
    );

    const calculatePercentCompleted = () => {
        // Returns the amount the progress line should be filled in percent.
        const totalSteps = props.steps.length;
        const newTotal = totalSteps - 1;
        const percent = Math.round((currentStepIndex() / newTotal) * 100);
        
        return percent;
    };

    const dynamicProgressBarWidth = () => {
        // Adjusts width based on the number of steps.
        if (props.steps.length > 4) {
            return '100%';
        }

        const width = 40 + (props.steps.length * 10);
        return `${width}%`;
    };

    const StepOverview = () => (
        <div class="
                        flex flex-row justify-between items-center gap-4 relative mx-auto mb-[6.5rem]
                        max-tablet:w-full max-tablet:mb-[4rem]
                        tablet:w-[var(--dynamic-width)]
                        before:content-[''] before:w-full before:h-[4px] before:bg-gray-light
                        before:absolute before:bottom-[calc(50%-2px)] before:left-0 before:z-0
                        after:content-[''] after:h-[4px] after:bg-primary 
                        after:absolute after:bottom-[calc(50%-2px)] after:left-0 after:z-0 
                        after:transition-[width] after:duration-200 after:ease-linear
                        after:w-[var(--after-width)]
                    "
        style={{
            '--dynamic-width': dynamicProgressBarWidth(),
            '--after-width': `${calculatePercentCompleted()}%`                                           
        }}
        >
            <For each={props.steps}>{(step, index) => {
                    
                return (
                    <div class="flex relative flex-col items-center gap-[0.5rem]">
                        <div class="z-[1] w-[4.5rem] h-[4.5rem] min-w-[4.5rem] min-h-[4.5rem] max-w-[4.5rem] max-h-[4.5rem] flex justify-center items-center p-0 rounded-full border-[3px] transition-all duration-200 ease-in-out"
                            classList={{
                                'bg-primary': index() < currentStepIndex(),
                                'text-white': index() < currentStepIndex(),
                                'border-primary': index() <= currentStepIndex(),
                                'bg-white': index() >= currentStepIndex(),
                                'text-gray-dark': index() >= currentStepIndex(),
                                'border-gray-light': index() > currentStepIndex(),
                                'delay-0': index() < currentStepIndex(),
                                'delay-200': index() >= currentStepIndex(),
                            }}
                        >
                            <Text fontSize={'normal'} color='defer' noBlockSpacing={true}>
                                {`${index() + 1}`}
                            </Text>
                        </div>

                        <Show when={step.stepName && !props.hideStepNames}>
                            <div class="absolute top-[4.5rem] mt-[0.5rem] w-max text-center max-w-[12rem] max-tablet:hidden">
                                <Text fontSize='footerSmall' color={theme.palette.darkGray}>
                                    {step.stepName}
                                </Text>
                            </div>
                        </Show>
                    </div>
                );
            }}</For>
        </div>
    );

    const Step = () => (
        <>
            <StepHeading />

            <StyledFlexColumn gap='1rem'>
                <StepFields />
            </StyledFlexColumn>
        </>
    );

    const StepHeading = () => (
        <Show when={formHasMultipleSteps() && props.steps[ currentStepIndex() ].stepName && !props.hideStepNames}>
            <Heading tag='h3'>
                {props.steps[ currentStepIndex() ]?.stepName}
            </Heading>
        </Show>
    );

    const renderSuccessMessage = () => {
        return (
            <div class="p-l tablet:p-xxxl flex flex-col items-center bg-white rounded-[var(--radius-large)]">
                <div class="mb-s w-[80px] h-[80px] tablet:w-[100px] tablet:h-[100px]">
                    <CheckCircleIcon height="100%" width="100%" />
                </div>
                {props.successMessageTitle && (
                    <Heading tag="h4" variant="large" align="center" noBlockSpacing={false}>
                        {props.successMessageTitle}
                    </Heading>
                )}
                <Text noBlockSpacing={false} align="center" fontSize="normal">
                    {props.successMessageText
                        ? props.successMessageText
                        : localize(
                            'form-submit-success',
                            'The form was successfully submitted'
                        )}
                </Text>
            </div>
        );
    };

    return (
        <ErrorCatcher componentName='Forms'>
            <Show when={success()}>{renderSuccessMessage()}</Show>

            {/* Only render the form if the privacy policy page is set and the fields are loaded */}
            <Show when={!success() && privacyPolicyPage && props.steps[currentStepIndex()].fields && !isDisabledEvent }>
                <div class="w-full max-w-[65rem] scroll-mt-[10rem] mt-[5rem] first:mt-0" ref={formRef}>
                    <Show when={searchParams?.product}>
                        <Text noBlockSpacing={false} fontSize='normal' fontStyle='italic' color='darkestGray' displayRedVerticalLine={false}>
                            {productContactTitle()}
                        </Text>
                    </Show>

                    <Show when={formHasMultipleSteps()}>
                        <StepOverview />
                    </Show>

                    <form class="flex flex-col gap-[1rem] mb-[4rem] max-mobile:justify-center max-mobile:m-0">
                        <Step/>

                        <Text noBlockSpacing={true} fontSize='small' fontStyle='italic' color='darkGray'>{`*${localize('required-fields', 'required fields')}`}</Text>
                        <StyledVerticalSpace size={2} />

                        <Show when={isFinalStep()}>
                            <FinalStepFixedElements />
                        </Show>

                        <Show
                            when={formHasMultipleSteps()}
                            fallback={(
                                <Button
                                    label={localize('form-submit', 'Submit')}
                                    variant='primary'
                                    onClick={(e) => handleSend(e)}
                                    customCss='margin-left: auto;'
                                    disabled={loading()}
                                    type="submit"
                                />
                            )}
                        >
                            <NavigationButtons />
                        </Show>
                    </form>
                </div>
            </Show>
        </ErrorCatcher>
    );
};

